import { computed, ref } from 'vue';
import PostSaleCaseTopicsAPI from 'dashboard/api/postSaleCaseTopics';

export function usePostSaleCaseTopics({ t, notify }) {
  const catalog = ref({});
  const topics = ref([]);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref('');
  const selectedTopic = ref(null);
  const showTopicForm = ref(false);

  const errorMessage = requestError =>
    requestError?.response?.data?.message ||
    requestError?.response?.data?.error ||
    t('POST_SALE.TOPICS.ALERTS.LOAD_ERROR');

  const topicOptions = computed(() => catalog.value.topics || []);

  const clear = () => {
    catalog.value = {};
    topics.value = [];
    selectedTopic.value = null;
    showTopicForm.value = false;
    error.value = '';
  };

  const load = async postSaleCaseId => {
    if (!postSaleCaseId) return;
    loading.value = true;
    error.value = '';

    try {
      const [{ data: catalogData }, { data: topicsData }] = await Promise.all([
        PostSaleCaseTopicsAPI.catalog(postSaleCaseId),
        PostSaleCaseTopicsAPI.list(postSaleCaseId),
      ]);
      catalog.value = catalogData?.data || {};
      topics.value = topicsData?.data || [];
    } catch (requestError) {
      clear();
      error.value = errorMessage(requestError);
      notify(error.value);
    } finally {
      loading.value = false;
    }
  };

  const openCreate = () => {
    selectedTopic.value = null;
    showTopicForm.value = true;
  };

  const openEdit = topic => {
    selectedTopic.value = topic;
    showTopicForm.value = true;
  };

  const closeEditor = () => {
    selectedTopic.value = null;
    showTopicForm.value = false;
  };

  const save = async (postSaleCaseId, payload) => {
    saving.value = true;
    try {
      const request = selectedTopic.value
        ? PostSaleCaseTopicsAPI.update(
            postSaleCaseId,
            selectedTopic.value.id,
            payload.topic_code,
            {
              ...payload.attributes,
              expected_version: selectedTopic.value.version,
            }
          )
        : PostSaleCaseTopicsAPI.create(
            postSaleCaseId,
            payload.topic_code,
            payload.attributes
          );
      const { data } = await request;
      const saved = data.data;
      topics.value = selectedTopic.value
        ? topics.value.map(topic => (topic.id === saved.id ? saved : topic))
        : [saved, ...topics.value];
      notify(
        selectedTopic.value
          ? t('POST_SALE.TOPICS.ALERTS.UPDATED')
          : t('POST_SALE.TOPICS.ALERTS.CREATED')
      );
      closeEditor();
      return saved;
    } catch (requestError) {
      notify(errorMessage(requestError));
      throw requestError;
    } finally {
      saving.value = false;
    }
  };

  const remove = async (postSaleCaseId, topic) => {
    saving.value = true;
    try {
      await PostSaleCaseTopicsAPI.destroy(
        postSaleCaseId,
        topic.id,
        topic.version
      );
      topics.value = topics.value.filter(item => item.id !== topic.id);
      if (selectedTopic.value?.id === topic.id) closeEditor();
      notify(t('POST_SALE.TOPICS.ALERTS.DELETED'));
    } catch (requestError) {
      notify(errorMessage(requestError));
      throw requestError;
    } finally {
      saving.value = false;
    }
  };

  return {
    catalog,
    clear,
    closeEditor,
    error,
    load,
    loading,
    openCreate,
    openEdit,
    remove,
    save,
    saving,
    selectedTopic,
    showTopicForm,
    topicOptions,
    topics,
  };
}
